import React from 'react';
import { Star, MapPin, Phone, Mail, Clock, Shield, Users, Building } from 'lucide-react';
import type { TravelAgency } from '../../types';
import { getImageUrl } from '../../utils/imageUrl';
import { getAgencyPageUrl } from '../../utils/agencyPageUrl';

interface AgencyInfoCardProps {
  agency?: TravelAgency;
  className?: string;
}

// Définir un thème par défaut
const defaultTheme = {
  primaryColor: '#3B82F6',   // blue-600
  secondaryColor: '#1E40AF', // blue-800
  accentColor: '#EAB308',    // yellow-500
  backgroundColor: '#F3F4F6', // gray-100
  textColor: '#111827'       // gray-900
};

export default function AgencyInfoCard({ agency, className = '' }: AgencyInfoCardProps) {
  if (!agency) {
    return (
      <div className={`bg-white rounded-lg shadow-lg p-6 text-center ${className}`}>
        <Building className="w-12 h-12 text-gray-400 mx-auto mb-2" />
        <p className="text-gray-500">Informations de l'agence non disponibles</p>
      </div>
    );
  }
  
  // Fusionner le thème de l'agence avec le thème par défaut
  const theme = {
    ...defaultTheme,
    ...agency.theme
  };

  return (
    <div className={`bg-white rounded-lg shadow-lg overflow-hidden ${className}`}>
      {/* En-tête avec image de couverture */}
      <div 
        className="h-32 relative"
        style={{ 
          background: `linear-gradient(to right, ${theme.primaryColor}, ${theme.secondaryColor})` 
        }}
      >
        <div className="absolute -bottom-10 left-6 flex items-end">
          {agency.logo ? (
            <div 
              style={{ backgroundColor: theme.secondaryColor }}
              className="w-20 h-20 rounded-lg border-4 border-white shadow-md p-2"
            >
              <img
                src={getImageUrl(agency.logo)}
                alt={agency.name}
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div 
              style={{ backgroundColor: theme.secondaryColor }}
              className="w-20 h-20 rounded-lg border-4 border-white shadow-md flex items-center justify-center"
            >
              <Building className="w-10 h-10 text-white" />
            </div>
          )}
        </div>
      </div>

      {/* Contenu */}
      <div className="pt-14 px-6 pb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-bold" style={{ color: theme.primaryColor }}>{agency.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              {agency.verified && (
                <>
                  <span className="text-gray-400">•</span>
                  <span className="flex items-center gap-1 text-sm" style={{ color: theme.primaryColor }}>
                    <Shield className="w-4 h-4" />
                    Vérifié
                  </span>
                </>
              )}
            </div>
          </div>
          <a
            href={getAgencyPageUrl(agency)}
            style={{ 
              backgroundColor: theme.primaryColor,
              color: theme.secondaryColor 
            }}
            className="px-4 py-2 text-sm font-medium rounded-lg hover:opacity-90 transition inline-block"
          >
            Voir l'agence
          </a>
        </div>

        {/* Informations de contact */}
        <div className="space-y-3 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-400" />
            <span>{agency.address}</span>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-gray-400" />
            <span>{agency.phone}</span>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-gray-400" />
            <span>{agency.email}</span>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t">
          <div className="text-center">
            <Clock className="w-5 h-5 mx-auto mb-1" />
            <div className="text-sm font-medium">{agency.yearsExperience}+ ans</div>
            <div className="text-xs text-gray-500">d'expérience</div>
          </div>
          <div className="text-center">
            <Users className="w-5 h-5 mx-auto mb-1" />
            <div className="text-sm font-medium">{agency.satisfiedTravelers}+</div>
            <div className="text-xs text-gray-500">voyageurs</div>
          </div>
        </div>
      </div>
    </div>
  );
}